import { z } from 'zod';

import { WikiActiveDispatchSchema, WikiFailedDispatchSchema } from './wiki-dispatch.js';

/**
 * The 6 possible states of the wiki pipeline for a case.
 *
 * `incomplete_enrichment` (ajouté 2026-06-05) : l'ingest a réussi (wiki
 * consultable) mais une passe AVAL — lint ou review — a échoué. C'est un état
 * DÉGRADÉ non bloquant (ambre « à relancer »), PAS un arrêt fatal `interrupted`
 * (rouge). Distinction introduite après l'incident où un lint échoué affichait
 * tout le wiki en « Erreur » alors que les données ingest étaient disponibles.
 */
export const WikiStateSchema = z.enum([
  'never_run',
  'running',
  'interrupted',
  'incomplete_enrichment',
  'incomplete_new_pieces',
  'up_to_date',
]);
export type WikiState = z.infer<typeof WikiStateSchema>;

/** Shape of the active job when state = 'running'. */
export const WikiCurrentJobSchema = z.object({
  /** Which pipeline stage is actively running. */
  stage: z.enum(['ingest', 'lint', 'consolidation', 'review']),
  started_at: z.string().datetime(),
  /** 0–100 progress percentage, undefined when not computable. */
  progress_pct: z.number().min(0).max(100).optional(),
  /**
   * true tant que la ligne de phase courante est encore `pending` : aucun
   * worker ne l'a prise, le pipeline attend un permis Case Wiki. L'avocat lit
   * alors « En file d'attente » plutôt qu'une progression figée. Optionnel :
   * ajout rétrocompatible au contrat.
   */
  waiting: z.boolean().optional(),
});
export type WikiCurrentJob = z.infer<typeof WikiCurrentJobSchema>;

/** Last failure info, present when state = 'interrupted' or 'incomplete_enrichment'. */
export const WikiLastFailureSchema = z.object({
  failed_at: z.string().datetime(),
  error_message: z.string().nullable(),
});
export type WikiLastFailure = z.infer<typeof WikiLastFailureSchema>;

/** Last completed run info, present when state = 'up_to_date' or 'incomplete_new_pieces'. */
export const WikiLastCompletedRunSchema = z.object({
  completed_at: z.string().datetime(),
});
export type WikiLastCompletedRun = z.infer<typeof WikiLastCompletedRunSchema>;

/** Progress of a single pipeline phase (M098). For lint, consolidation and
 *  review, `done`/`total` are scoped to the CURRENT pipeline generation so
 *  reruns never inflate them. Ingest is the exception: its `done`/`total` are
 *  cumulative for the dossier, and `run_done`/`run_total` carry the current
 *  generation. */
export const WikiPhaseProgressSchema = z.object({
  done: z.number().int().min(0),
  total: z.number().int().min(0),
  /**
   * Pièces ayant échoué sans avoir été re-traitées avec succès (ingest only).
   * ADDITIF (`.optional()`) : les réponses antérieures sans ce champ restent
   * valides côté parse Zod front. Permet d'afficher « X pièces non analysées ».
   */
  failed: z.number().int().min(0).optional(),
  /**
   * Ingest only: pieces handled by the CURRENT pipeline generation, anchored
   * on the latest ingest phase row like the other phases' counters. `done`
   * stays cumulative for the dossier, so a 1-piece update on 999 analysed
   * pieces would read 99 % at launch; the progress percentage reads these.
   * Absent for dossiers analysed before M098 (no phase row).
   */
  run_done: z.number().int().min(0).optional(),
  /**
   * The pieces that phase's worker froze in its list (`payload.planned_pieces`
   * on the phase row), so a piece uploaded mid-run or a `documentIds`-targeted
   * run keeps the right denominator. Before a worker records it (phase still
   * pending, older rows): `run_done` + pieces still waiting for ingest while
   * the phase is active.
   */
  run_total: z.number().int().min(0).optional(),
});
export type WikiPhaseProgress = z.infer<typeof WikiPhaseProgressSchema>;

/**
 * Per-phase progress for the chip tooltip, derived from `case_wiki_runs`:
 *  - ingest: pieces with a terminal ingest item row / total ingerable
 *  - lint:   0 or 1 / 1
 *  - consolidation: consolidation passes done / total consolidation passes
 *  - review: review passes done / total active review passes
 */
export const WikiPipelineProgressSchema = z.object({
  ingest: WikiPhaseProgressSchema,
  lint: WikiPhaseProgressSchema,
  consolidation: WikiPhaseProgressSchema,
  review: WikiPhaseProgressSchema,
});
export type WikiPipelineProgress = z.infer<typeof WikiPipelineProgressSchema>;

/**
 * Response schema for GET /api/cases/:caseId/wiki/state.
 *
 * Single source of truth for the wiki pipeline state. Polled adaptively
 * by the frontend (2s when running, 10s interrupted, 60s calm).
 */
export const WikiStateResponseSchema = z.object({
  state: WikiStateSchema,
  /** Total ingerable pieces for this case. */
  total_pieces: z.number().int().min(0),
  /** Pieces that have a completed ingest run. */
  processed_pieces: z.number().int().min(0),
  /** Pieces not yet ingested (or added after the last completed run). */
  new_pieces_count: z.number().int().min(0),
  /** Non-null only when state = 'running'. */
  current_job: WikiCurrentJobSchema.nullable(),
  /** Non-null only when state = 'interrupted'. */
  last_failure: WikiLastFailureSchema.nullable(),
  /** Non-null when state = 'up_to_date' or 'incomplete_new_pieces'. */
  last_completed_run: WikiLastCompletedRunSchema.nullable(),
  /** ISO datetime of last successful ingest completion. Null if never completed. */
  last_ingest_completed_at: z.string().datetime().nullable(),
  /** ISO datetime of last successful lint completion. Null if never completed. */
  last_lint_completed_at: z.string().datetime().nullable(),
  /** ISO datetime of last successful consolidation completion. Null if never completed. */
  last_consolidation_completed_at: z.string().datetime().nullable(),
  /** ISO datetime of last successful review completion. Null if never completed. */
  last_review_completed_at: z.string().datetime().nullable(),
  /** Per-phase progress for the compact chip + tooltip (M098). */
  pipeline_progress: WikiPipelineProgressSchema,
  /** Pending durable launch request, separate from the pipeline lifecycle state. */
  active_dispatch: WikiActiveDispatchSchema.nullable(),
  /** Last terminal dispatch failure; retry remains an explicit launch action. */
  failed_dispatch: WikiFailedDispatchSchema.nullable().optional(),
  /**
   * True when the pipeline is in a settled terminal state (nothing in flight).
   * Drives the frontend polling cadence explicitly instead of inferring it
   * from `state` — equals `state !== 'running'`, but exposed so the front never
   * has to re-derive "is anything moving?". When false, poll fast.
   */
  is_terminal_stable: z.boolean(),
});
export type WikiStateResponse = z.infer<typeof WikiStateResponseSchema>;
